import { type Address, type PublicClient } from 'viem'
import { eEarnVaultImplementationAbi } from 'abis/evm/eEarnVaultImplementation'
import { eVaultImplementationAbi } from 'abis/evm/eVaultImplementation'
import type { NormalizedEVaultInfo } from './types'
import { fetchAssetInfo, calculateTotalAssetsUSD, calculateStrategyAPY } from '../shared/onchainHelpers'

/**
 * Calculate weighted APY for a single vault from its strategies
 */
async function calculateVaultAPY(publicClient: PublicClient, vaultAddress: Address): Promise<number> {
  try {
    // Step 1: Get withdraw queue length
    const queueLength = await publicClient.readContract({
      address: vaultAddress,
      abi: eEarnVaultImplementationAbi,
      functionName: 'withdrawQueueLength',
    })

    if (!queueLength || queueLength === 0n) {
      return 0
    }

    // Step 2: Get all strategy addresses
    const strategyAddresses: Address[] = []
    for (let i = 0; i < Number(queueLength); i++) {
      const strategyAddr = await publicClient.readContract({
        address: vaultAddress,
        abi: eEarnVaultImplementationAbi,
        functionName: 'withdrawQueue',
        args: [BigInt(i)],
      })
      strategyAddresses.push(strategyAddr as Address)
    }

    if (strategyAddresses.length === 0) {
      return 0
    }

    // Step 3: Get strategy data (shares, interestRate, totalBorrows, totalAssets, interestFee)
    const strategies: Array<{
      shares: bigint
      interestRate: bigint
      totalBorrows: bigint
      totalAssets: bigint
      interestFee?: bigint
    }> = []

    for (const strategyAddr of strategyAddresses) {
      const [config, interestRate, totalBorrows, totalAssets, interestFee] = await Promise.all([
        publicClient.readContract({
          address: vaultAddress,
          abi: eEarnVaultImplementationAbi,
          functionName: 'config',
          args: [strategyAddr],
        }),
        publicClient.readContract({
          address: strategyAddr,
          abi: eVaultImplementationAbi,
          functionName: 'interestRate',
        }),
        publicClient.readContract({
          address: strategyAddr,
          abi: eVaultImplementationAbi,
          functionName: 'totalBorrows',
        }),
        publicClient.readContract({
          address: strategyAddr,
          abi: eVaultImplementationAbi,
          functionName: 'totalAssets',
        }),
        publicClient.readContract({
          address: strategyAddr,
          abi: eVaultImplementationAbi,
          functionName: 'interestFee',
        }),
      ])

      const shares = (config as any)[0] as bigint

      strategies.push({
        shares,
        interestRate: interestRate as bigint,
        totalBorrows: totalBorrows as bigint,
        totalAssets: totalAssets as bigint,
        interestFee: BigInt(interestFee as number | bigint),
      })
    }

    // Step 4: Calculate weighted APY
    let totalShares = 0n
    const strategyAPYs: { shares: bigint; apy: number }[] = []

    for (const { shares, interestRate, totalBorrows, totalAssets, interestFee } of strategies) {
      if (totalAssets > 0n && shares > 0n) {
        const strategyAPY = calculateStrategyAPY(interestRate, totalBorrows, totalAssets, interestFee)
        totalShares += shares
        strategyAPYs.push({ shares, apy: strategyAPY })
      }
    }

    if (totalShares > 0n && strategyAPYs.length > 0) {
      let weightedAPY = 0
      for (const { shares, apy } of strategyAPYs) {
        const weight = Number(shares) / Number(totalShares)
        weightedAPY += apy * weight
      }
      return weightedAPY
    }

    return 0
  } catch (error) {
    console.warn(`Failed to calculate APY for vault ${vaultAddress}:`, error)
    return 0
  }
}

/**
 * Fetch EVault info directly from onchain (fallback when indexer is unavailable)
 */
export async function fetchEVaultOnchain(
  publicClient: PublicClient,
  chainId: number,
  address: Address,
): Promise<NormalizedEVaultInfo> {
  const contracts = [
    {
      address,
      abi: eEarnVaultImplementationAbi,
      functionName: 'name',
    },
    {
      address,
      abi: eEarnVaultImplementationAbi,
      functionName: 'symbol',
    },
    {
      address,
      abi: eEarnVaultImplementationAbi,
      functionName: 'decimals',
    },
    {
      address,
      abi: eEarnVaultImplementationAbi,
      functionName: 'asset',
    },
    {
      address,
      abi: eEarnVaultImplementationAbi,
      functionName: 'totalAssets',
    },
    {
      address,
      abi: eEarnVaultImplementationAbi,
      functionName: 'totalSupply',
    },
    {
      address,
      abi: eEarnVaultImplementationAbi,
      functionName: 'owner',
    },
    {
      address,
      abi: eEarnVaultImplementationAbi,
      functionName: 'curator',
    },
    {
      address,
      abi: eEarnVaultImplementationAbi,
      functionName: 'guardian',
    },
    {
      address,
      abi: eEarnVaultImplementationAbi,
      functionName: 'timelock',
    },
    {
      address,
      abi: eEarnVaultImplementationAbi,
      functionName: 'fee',
    },
  ] as const

  const results = await publicClient.multicall({ contracts })

  const name = results[0].status === 'success' ? (results[0].result as string) : 'Unknown Vault'
  const symbol = results[1].status === 'success' ? (results[1].result as string) : 'UNKNOWN'
  const decimals = results[2].status === 'success' ? (results[2].result as number) : 18
  const asset = results[3].status === 'success' ? (results[3].result as Address) : null
  const totalAssets = results[4].status === 'success' ? (results[4].result as bigint) : 0n
  const totalShares = results[5].status === 'success' ? (results[5].result as bigint) : 0n
  const owner = results[6].status === 'success' ? (results[6].result as Address) : null
  const curator = results[7].status === 'success' ? (results[7].result as Address) : null
  const guardian = results[8].status === 'success' ? (results[8].result as Address) : null
  const timelock = results[9].status === 'success' ? (results[9].result as bigint) : 0n
  const fee = results[10].status === 'success' ? (results[10].result as bigint) : 0n

  // Fetch asset info and price if we have an asset address
  let assetName: string | null = null
  let assetSymbol: string | null = null
  let assetDecimals: number | null = null
  let totalAssetsUSD = 0

  if (asset) {
    const assetInfo = await fetchAssetInfo(publicClient, asset, address)
    if (assetInfo) {
      assetSymbol = assetInfo.symbol
      assetDecimals = assetInfo.decimals
      assetName = assetInfo.symbol // Use symbol as name fallback
      totalAssetsUSD = calculateTotalAssetsUSD(totalAssets, assetInfo.decimals, assetInfo.priceUSD)
    }
  }

  // Calculate APY from strategies
  const apyCurrent = await calculateVaultAPY(publicClient, address)

  return {
    chainId,
    address,
    name,
    symbol,
    decimals,
    asset,
    assetName,
    assetSymbol,
    assetDecimals,
    totalShares,
    totalAssets,
    totalAssetsUSD,
    availableAssets: totalAssets, // Assume all assets are available
    availableAssetsUSD: totalAssetsUSD, // Same as totalAssetsUSD for available assets
    apy7d: null,
    apy30d: null,
    apy90d: null,
    apyCurrent, // Calculated from strategies
    strategies: [], // Not available onchain without additional calls
    owner,
    curator,
    guardian,
    performanceFee: fee.toString(),
    timelock: timelock.toString(),
    supplyQueue: null, // Would require additional calls
    source: 'onchain',
  }
}

/**
 * Batch fetch multiple EVaults from onchain
 * Optimized to fetch all vault data in parallel batches for fast loading
 */
export async function fetchEVaultsOnchain(
  publicClient: PublicClient,
  chainId: number,
  addresses: Address[],
): Promise<Map<Address, NormalizedEVaultInfo>> {
  const vaultMap = new Map<Address, NormalizedEVaultInfo>()

  if (addresses.length === 0) {
    return vaultMap
  }

  try {
    // Step 1: Batch fetch basic vault info for all vaults in one multicall
    const vaultContracts = addresses.flatMap((address) => [
      {
        address,
        abi: eEarnVaultImplementationAbi,
        functionName: 'name' as const,
      },
      {
        address,
        abi: eEarnVaultImplementationAbi,
        functionName: 'symbol' as const,
      },
      {
        address,
        abi: eEarnVaultImplementationAbi,
        functionName: 'decimals' as const,
      },
      {
        address,
        abi: eEarnVaultImplementationAbi,
        functionName: 'asset' as const,
      },
      {
        address,
        abi: eEarnVaultImplementationAbi,
        functionName: 'totalAssets' as const,
      },
      {
        address,
        abi: eEarnVaultImplementationAbi,
        functionName: 'totalSupply' as const,
      },
      {
        address,
        abi: eEarnVaultImplementationAbi,
        functionName: 'owner' as const,
      },
      {
        address,
        abi: eEarnVaultImplementationAbi,
        functionName: 'curator' as const,
      },
      {
        address,
        abi: eEarnVaultImplementationAbi,
        functionName: 'guardian' as const,
      },
      {
        address,
        abi: eEarnVaultImplementationAbi,
        functionName: 'timelock' as const,
      },
      {
        address,
        abi: eEarnVaultImplementationAbi,
        functionName: 'fee' as const,
      },
    ])

    const results = await publicClient.multicall({ contracts: vaultContracts })

    // Step 2: Process results for each vault immediately (without waiting for asset info)
    // Asset info (symbols, decimals, prices) will be fetched by useTokensInfo in useEVaults
    for (let i = 0; i < addresses.length; i++) {
      const address = addresses[i]
      if (!address) continue // Skip if address is undefined
      const baseIndex = i * 11

      const nameResult = results[baseIndex]
      const symbolResult = results[baseIndex + 1]
      const decimalsResult = results[baseIndex + 2]
      const assetResult = results[baseIndex + 3]
      const totalAssetsResult = results[baseIndex + 4]
      const totalSharesResult = results[baseIndex + 5]
      const ownerResult = results[baseIndex + 6]
      const curatorResult = results[baseIndex + 7]
      const guardianResult = results[baseIndex + 8]
      const timelockResult = results[baseIndex + 9]
      const feeResult = results[baseIndex + 10]

      const name = nameResult?.status === 'success' ? (nameResult.result as string) : 'Unknown Vault'
      const symbol = symbolResult?.status === 'success' ? (symbolResult.result as string) : 'UNKNOWN'
      const decimals = decimalsResult?.status === 'success' ? (decimalsResult.result as number) : 18
      const asset = assetResult?.status === 'success' ? (assetResult.result as Address) : null
      const totalAssets = totalAssetsResult?.status === 'success' ? (totalAssetsResult.result as bigint) : 0n
      const totalShares = totalSharesResult?.status === 'success' ? (totalSharesResult.result as bigint) : 0n
      const owner = ownerResult?.status === 'success' ? (ownerResult.result as Address) : null
      const curator = curatorResult?.status === 'success' ? (curatorResult.result as Address) : null
      const guardian = guardianResult?.status === 'success' ? (guardianResult.result as Address) : null
      const timelock = timelockResult?.status === 'success' ? (timelockResult.result as bigint) : 0n
      const fee = feeResult?.status === 'success' ? (feeResult.result as bigint) : 0n

      // Asset info (symbol, decimals, prices) will be fetched separately by useTokensInfo
      // This avoids blocking the main vault fetch on slow/failing price oracle calls
      const assetSymbol = null
      const assetDecimals = null
      const totalAssetsUSD = 0 // Will be calculated once we have asset prices

      // Skip APY calculation for initial load - it will be calculated by useBatchEVaultAPY
      // This makes the initial load much faster
      const apyCurrent = null

      vaultMap.set(address, {
        chainId,
        address,
        name,
        symbol,
        decimals,
        asset,
        assetName: assetSymbol,
        assetSymbol,
        assetDecimals,
        totalShares,
        totalAssets,
        totalAssetsUSD,
        availableAssets: totalAssets,
        availableAssetsUSD: totalAssetsUSD,
        apy7d: null,
        apy30d: null,
        apy90d: null,
        apyCurrent, // Will be loaded progressively by useBatchEVaultAPY
        strategies: [],
        owner,
        curator,
        guardian,
        performanceFee: fee.toString(),
        timelock: timelock.toString(),
        supplyQueue: null,
        source: 'onchain',
      })
    }

    return vaultMap
  } catch (error) {
    console.error('[fetchEVaultsOnchain] Batch fetch failed:', error)
    // Return empty map on error
    return vaultMap
  }
}
